// src/pages/ChangePasswordPage.js
// =============================================================================
// ChangePasswordPage
// =============================================================================
//
// This page lets the authenticated user change their own password through the
// AuthContext.changePassword API.
//
// i18n note:
// - Validation and fallback messages owned by this frontend page are translated.
// - Server-provided error messages are still displayed as-is when present. This
//   keeps the backend contract unchanged and avoids guessing how backend errors
//   should be mapped before the auth API has a stable error-code layer.
// - Existing UX behavior is preserved: auto-focus on the current password input,
//   local form validation, loading state, clearing inputs after successful change,
//   and browser-history based cancel navigation.
//

import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../context/AuthContext";
import "./ChangePasswordPage.css";

const ChangePasswordPage = () => {
  const { t } = useTranslation(["common", "userSettings"]);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { changePassword } = useContext(AuthContext); // Get changePassword function from AuthContext

  /**
   * ✅ Auto-focus on first input field when page loads
   */
  useEffect(() => {
    document.getElementById("current-password")?.focus();
  }, []);

  /**
   * ✅ Handles password change submission.
   */
  const handleChangePassword = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!currentPassword || !newPassword) {
      setErrorMessage(t("userSettings:changePassword.messages.required"));
      return;
    }

    if (newPassword.length < 8) {
      setErrorMessage(t("userSettings:changePassword.messages.minLength", { min: 8 }));
      return;
    }

    setIsLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      setSuccessMessage(t("userSettings:changePassword.messages.success"));
      setCurrentPassword("");
      setNewPassword("");
    } catch (err) {
      console.error("[ChangePasswordPage] ❌ Failed to change password:", err);
      setErrorMessage(err.response?.data?.message || t("userSettings:changePassword.messages.genericError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="change-password-container">
      <h1 className="change-password-title">{t("userSettings:changePassword.title")}</h1>

      {successMessage && <p className="success-message">{successMessage}</p>}
      {errorMessage && <p className="error-message">{errorMessage}</p>}

      <form onSubmit={handleChangePassword} className="change-password-form">
        <label htmlFor="current-password">{t("userSettings:changePassword.fields.currentPassword")}</label>
        <input
          id="current-password"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          required
          disabled={isLoading}
        />

        <label htmlFor="new-password">{t("userSettings:changePassword.fields.newPassword")}</label>
        <input
          id="new-password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          required
          disabled={isLoading}
        />

        <div className="button-group">
          <button type="submit" className="update-button" disabled={isLoading}>
            {isLoading
              ? t("userSettings:changePassword.actions.updating")
              : t("userSettings:changePassword.actions.changePassword")}
          </button>
          <button type="button" className="cancel-button" onClick={() => window.history.back()}>
            {t("common:actions.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangePasswordPage;
